import {
  Add,
  Category,
  Delete,
  DirectionsCar,
  Domain,
  Edit,
  OpenInNew,
  Palette,
  Save,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import {
  dashboardCardSx,
  dashboardPagePaddingSx,
  dashboardPageSx,
  dashboardSectionTitleSx,
} from "../components/layout/dashboardStyles";
import { carRentalApi } from "../data/api/CarRentalApi";
import { openExperiencePreview } from "../utils/experiencePreview";
import {
  CarRentalCategory,
  CarRentalConfig,
  CarRentalReservationRequest,
  CarRentalReservationStatus,
  CarRentalVehicle,
} from "../data/models/Experience/ExperienceModels";
import { StoreDomain, StorePageConfig, StorePageId } from "../data/models/Experience/ExperienceSharedModels";
import {
  domainStatusColor,
  experiencePageLabels,
  experienceToggleGroupSx,
  mergeExperiencePages,
  SnackbarSeverity,
} from "./experienceAdmin/shared";

type CarTab = "settings" | "design" | "pages" | "seo" | "fleet" | "domains" | "reservations";

const basePages: StorePageConfig[] = [
  { id: "home", slug: "", title: "Inicio", navLabel: "Inicio", footerLabel: "Inicio", contentTitle: "Inicio", contentBody: "", showInNavbar: true, showInFooter: false, isEnabled: true },
  { id: "about", slug: "nosotros", title: "Nosotros", navLabel: "Nosotros", footerLabel: "Nosotros", contentTitle: "Nosotros", contentBody: "Cuenta la cobertura, flota y propuesta de valor de tu rentadora.", showInNavbar: true, showInFooter: true, isEnabled: true },
  { id: "contact", slug: "contactanos", title: "Contáctanos", navLabel: "Contáctanos", footerLabel: "Contáctanos", contentTitle: "Contáctanos", contentBody: "Comparte tus canales de atención, oficinas y horarios.", showInNavbar: true, showInFooter: true, isEnabled: true },
  { id: "terms", slug: "terminos-y-condiciones", title: "Términos y condiciones", navLabel: "Términos", footerLabel: "Términos y condiciones", contentTitle: "Términos y condiciones", contentBody: "Agrega aquí políticas de alquiler, seguros y devoluciones.", showInNavbar: false, showInFooter: true, isEnabled: true },
  { id: "privacy", slug: "politicas-de-privacidad", title: "Políticas de privacidad", navLabel: "Privacidad", footerLabel: "Políticas de privacidad", contentTitle: "Políticas de privacidad", contentBody: "Agrega aquí como recolectas, usas y proteges los datos de los arrendatarios.", showInNavbar: false, showInFooter: true, isEnabled: true },
];

const defaultConfig: Partial<CarRentalConfig> = {
  brandName: "",
  slug: "",
  templateId: "cars-default",
  experienceType: "car_rental",
  status: "draft",
  pages: basePages,
};

const defaultCategory: Partial<CarRentalCategory> = {
  name: "",
  description: "",
  isActive: true,
  sortOrder: 0,
};

const defaultVehicle: Partial<CarRentalVehicle> = {
  name: "",
  description: "",
  categoryId: "",
  pricePerDay: 0,
  transmission: "",
  passengerCapacity: 5,
  doors: 4,
  luggageCapacity: 2,
  hasAirConditioning: true,
  location: "",
  imageUrl: "",
  availabilityNotes: "",
  isActive: true,
  sortOrder: 0,
};

const tabPaths: Record<CarTab, string> = {
  settings: "/dashboard/car-rental/settings",
  design: "/dashboard/car-rental/design",
  pages: "/dashboard/car-rental/pages",
  seo: "/dashboard/car-rental/seo",
  fleet: "/dashboard/car-rental/fleet",
  domains: "/dashboard/car-rental/domains",
  reservations: "/dashboard/car-rental/reservations",
};

const reservationStatusLabels: Record<CarRentalReservationStatus, string> = {
  pending: "Pendiente",
  contacted: "Contactado",
  confirmed: "Confirmado",
  cancelled: "Cancelado",
};

const reservationStatusColor = (status: CarRentalReservationStatus) => {
  if (status === "confirmed") return "success" as const;
  if (status === "cancelled") return "error" as const;
  if (status === "contacted") return "warning" as const;
  return "default" as const;
};

const CarRentalPage = ({ initialTab = "settings" }: { initialTab?: CarTab }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<CarTab>(initialTab);
  const [config, setConfig] = useState<Partial<CarRentalConfig>>(defaultConfig);
  const [categories, setCategories] = useState<CarRentalCategory[]>([]);
  const [vehicles, setVehicles] = useState<CarRentalVehicle[]>([]);
  const [domains, setDomains] = useState<StoreDomain[]>([]);
  const [reservations, setReservations] = useState<CarRentalReservationRequest[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<StorePageId>("home");
  const [categoryDraft, setCategoryDraft] = useState<Partial<CarRentalCategory>>(defaultCategory);
  const [vehicleDraft, setVehicleDraft] = useState<Partial<CarRentalVehicle>>(defaultVehicle);
  const [categoryEditingId, setCategoryEditingId] = useState<string | undefined>();
  const [vehicleEditingId, setVehicleEditingId] = useState<string | undefined>();
  const [domainDraft, setDomainDraft] = useState("");
  const [loadError, setLoadError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<SnackbarSeverity>("success");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<CarRentalCategory | undefined>();
  const [vehicleToDelete, setVehicleToDelete] = useState<CarRentalVehicle | undefined>();

  const pages = mergeExperiencePages(basePages, config.pages);
  const selectedPage = pages.find((page) => page.id === selectedPageId) || pages[0];

  const showSnackbar = (message: string, severity: SnackbarSeverity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const updateSelectedPage = (patch: Partial<StorePageConfig>) => {
    setConfig((current) => ({
      ...current,
      pages: mergeExperiencePages(basePages, current.pages).map((page) =>
        page.id === selectedPageId ? { ...page, ...patch } : page,
      ),
    }));
  };

  const resetCategoryDialog = () => {
    setCategoryEditingId(undefined);
    setCategoryDraft(defaultCategory);
    setCategoryDialogOpen(false);
  };

  const resetVehicleDialog = () => {
    setVehicleEditingId(undefined);
    setVehicleDraft(defaultVehicle);
    setVehicleDialogOpen(false);
  };

  const openCategoryDialog = (category?: CarRentalCategory) => {
    setCategoryEditingId(category?.id);
    setCategoryDraft(category ? { ...category } : defaultCategory);
    setCategoryDialogOpen(true);
  };

  const openVehicleDialog = (vehicle?: CarRentalVehicle) => {
    setVehicleEditingId(vehicle?.id);
    setVehicleDraft(vehicle ? { ...vehicle } : defaultVehicle);
    setVehicleDialogOpen(true);
  };

  const load = async () => {
    const [configResult, categoriesResult, vehiclesResult, domainsResult, reservationsResult] = await Promise.all([
      carRentalApi.config.requestHard(null),
      carRentalApi.categories.requestHard(null),
      carRentalApi.vehicles.requestHard(null),
      carRentalApi.domains.requestHard(null),
      carRentalApi.reservations.requestHard(null),
    ]);

    if (configResult.isSuccess && configResult.data) {
      setConfig({
        ...defaultConfig,
        ...configResult.data,
        pages: mergeExperiencePages(basePages, configResult.data.pages),
      });
    }
    if (categoriesResult.isSuccess) setCategories(categoriesResult.data || []);
    if (vehiclesResult.isSuccess) setVehicles(vehiclesResult.data || []);
    if (domainsResult.isSuccess) setDomains(domainsResult.data || []);
    if (reservationsResult.isSuccess) setReservations(reservationsResult.data || []);

    setLoadError(
      configResult.messageError ||
        categoriesResult.messageError ||
        vehiclesResult.messageError ||
        domainsResult.messageError ||
        reservationsResult.messageError ||
        "",
    );
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => setTab(initialTab), [initialTab]);

  const saveConfig = async (publish = false) => {
    const result = publish
      ? await carRentalApi.publish.requestHard({ config })
      : await carRentalApi.updateConfig.requestHard({ config });
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos guardar la configuración.", "error");
      return;
    }
    showSnackbar(publish ? "Mi renta de carros publicada." : "Configuración guardada.", "success");
    await load();
  };

  const saveCategory = async () => {
    if (!String(categoryDraft.name || "").trim()) {
      showSnackbar("El nombre de la categoría es requerido.", "error");
      return;
    }

    const result = categoryEditingId
      ? await carRentalApi.updateCategory(categoryEditingId).requestHard({ category: categoryDraft })
      : await carRentalApi.createCategory.requestHard({ category: categoryDraft });

    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos guardar la categoría.", "error");
      return;
    }

    showSnackbar(categoryEditingId ? "Categoría actualizada." : "Categoría creada.", "success");
    resetCategoryDialog();
    await load();
  };

  const saveVehicle = async () => {
    if (!String(vehicleDraft.name || "").trim()) {
      showSnackbar("El nombre del vehículo es requerido.", "error");
      return;
    }
    if (!String(vehicleDraft.location || "").trim()) {
      showSnackbar("La ubicación del vehículo es requerida.", "error");
      return;
    }

    const result = vehicleEditingId
      ? await carRentalApi.updateVehicle(vehicleEditingId).requestHard({ vehicle: vehicleDraft })
      : await carRentalApi.createVehicle.requestHard({ vehicle: vehicleDraft });

    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos guardar el vehículo.", "error");
      return;
    }

    showSnackbar(vehicleEditingId ? "Vehículo actualizado." : "Vehículo creado.", "success");
    resetVehicleDialog();
    await load();
  };

  const deleteCategory = async () => {
    if (!categoryToDelete) return;
    const result = await carRentalApi.deleteCategory(categoryToDelete.id).requestHard(null);
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos eliminar la categoría.", "error");
      return;
    }
    setCategoryToDelete(undefined);
    showSnackbar("Categoría eliminada.", "success");
    await load();
  };

  const deleteVehicle = async () => {
    if (!vehicleToDelete) return;
    const result = await carRentalApi.deleteVehicle(vehicleToDelete.id).requestHard(null);
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos eliminar el vehículo.", "error");
      return;
    }
    setVehicleToDelete(undefined);
    showSnackbar("Vehículo eliminado.", "success");
    await load();
  };

  const addDomain = async () => {
    if (!domainDraft.trim()) {
      showSnackbar("El dominio es requerido.", "error");
      return;
    }
    const result = await carRentalApi.addDomain.requestHard({ hostname: domainDraft.trim() });
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos agregar el dominio.", "error");
      return;
    }
    setDomainDraft("");
    setDomainDialogOpen(false);
    showSnackbar("Dominio agregado. Revisa las instrucciones DNS.", "success");
    await load();
  };

  const verifyDomain = async (hostname: string) => {
    const result = await carRentalApi.verifyDomain(hostname).requestHard(null);
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos verificar el dominio.", "error");
      return;
    }
    showSnackbar(
      result.data?.status === "active" ? "Dominio verificado." : "Aún no encontramos el DNS esperado.",
      result.data?.status === "active" ? "success" : "error",
    );
    await load();
  };

  const updateReservationStatus = async (reservationId: string, status: CarRentalReservationStatus) => {
    const result = await carRentalApi.updateReservationStatus(reservationId).requestHard({ status });
    if (!result.isSuccess) {
      showSnackbar(result.messageError || "No pudimos actualizar la reserva.", "error");
      return;
    }
    showSnackbar("Estado de la reserva actualizado.", "success");
    await load();
  };

  return (
    <Box sx={dashboardPageSx}>
      <Stack spacing={2} sx={dashboardPagePaddingSx}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
          <Box>
            <Typography variant="h4" sx={dashboardSectionTitleSx}>
              Mi renta de carros
            </Typography>
            <Typography color="text.secondary">
              Administra flota, dominios y reservas con la misma estructura visual de Mi tienda.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => openExperiencePreview("car_rental", { config, categories, vehicles })}
            >
              Vista previa
            </Button>
            <Button variant="outlined" startIcon={<Save />} onClick={() => void saveConfig(false)}>
              Guardar
            </Button>
            <Button variant="contained" onClick={() => void saveConfig(true)}>
              Publicar
            </Button>
          </Stack>
        </Stack>

        {loadError ? <Alert severity="error">{loadError}</Alert> : null}

        <ToggleButtonGroup
          exclusive
          value={tab}
          sx={experienceToggleGroupSx}
          onChange={(_, value) => {
            if (!value) return;
            const nextTab = value as CarTab;
            setTab(nextTab);
            navigate(tabPaths[nextTab]);
          }}
        >
          <ToggleButton value="settings">Configuración</ToggleButton>
          <ToggleButton value="design">Diseño</ToggleButton>
          <ToggleButton value="pages">Páginas</ToggleButton>
          <ToggleButton value="seo">SEO</ToggleButton>
          <ToggleButton value="fleet">Flota</ToggleButton>
          <ToggleButton value="domains">Dominios</ToggleButton>
          <ToggleButton value="reservations">Reservas</ToggleButton>
        </ToggleButtonGroup>

        {tab === "settings" && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">Configuración general</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Datos base para la marca, slug, textos comerciales y canales de contacto.
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Marca" value={config.brandName || ""} onChange={(event) => setConfig({ ...config, brandName: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Slug" value={config.slug || ""} onChange={(event) => setConfig({ ...config, slug: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Hero title" value={config.heroTitle || ""} onChange={(event) => setConfig({ ...config, heroTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Hero subtitle" value={config.heroSubtitle || ""} onChange={(event) => setConfig({ ...config, heroSubtitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="WhatsApp" value={config.contactWhatsapp || ""} onChange={(event) => setConfig({ ...config, contactWhatsapp: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Correo" value={config.contactEmail || ""} onChange={(event) => setConfig({ ...config, contactEmail: event.target.value })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "design" && (
          <Stack spacing={2}>
            <Alert severity="info">
              Esta vertical debe respetar el patron de Mi tienda: navegacion superior clara, cards limpias, modales para CRUD y snackbar para feedback.
            </Alert>
            <Card sx={dashboardCardSx}>
              <CardContent>
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Palette fontSize="small" />
                    <Typography variant="h6">Diseño visual</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Color principal" value={config.primaryColor || ""} onChange={(event) => setConfig({ ...config, primaryColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Color secundario" value={config.secondaryColor || ""} onChange={(event) => setConfig({ ...config, secondaryColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Fondo" value={config.backgroundColor || ""} onChange={(event) => setConfig({ ...config, backgroundColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Color de texto" value={config.textColor || ""} onChange={(event) => setConfig({ ...config, textColor: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Logo URL" value={config.logoUrl || ""} onChange={(event) => setConfig({ ...config, logoUrl: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Hero image URL" value={config.heroImageUrl || ""} onChange={(event) => setConfig({ ...config, heroImageUrl: event.target.value })} />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )}

        {tab === "pages" && selectedPage && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">Páginas internas</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Edita el contenido institucional que acompana la experiencia de renta.
                    </Typography>
                  </Box>
                  <TextField
                    select
                    label="Página"
                    value={selectedPageId}
                    onChange={(event) => setSelectedPageId(event.target.value as StorePageId)}
                    sx={{ minWidth: { xs: "100%", md: 220 } }}
                  >
                    {pages.map((page) => (
                      <MenuItem key={page.id} value={page.id}>
                        {experiencePageLabels[page.id]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Título visible" value={selectedPage.title} onChange={(event) => updateSelectedPage({ title: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Slug" value={selectedPage.slug} onChange={(event) => updateSelectedPage({ slug: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Título del contenido" value={selectedPage.contentTitle || ""} onChange={(event) => updateSelectedPage({ contentTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Label navbar" value={selectedPage.navLabel || ""} onChange={(event) => updateSelectedPage({ navLabel: event.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={5} label="Contenido" value={selectedPage.contentBody || ""} onChange={(event) => updateSelectedPage({ contentBody: event.target.value })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "seo" && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">SEO y metadatos</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configura los metadatos básicos para la experiencia pública.
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="SEO title" value={config.seoTitle || ""} onChange={(event) => setConfig({ ...config, seoTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Tab title" value={config.tabTitle || ""} onChange={(event) => setConfig({ ...config, tabTitle: event.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline minRows={3} label="SEO description" value={config.seoDescription || ""} onChange={(event) => setConfig({ ...config, seoDescription: event.target.value })} />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "fleet" && (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={4}>
              <Card sx={dashboardCardSx}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Category fontSize="small" />
                        <Typography variant="h6">Categorías</Typography>
                      </Stack>
                      <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openCategoryDialog()}>
                        Crear categoría
                      </Button>
                    </Stack>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categories.map((category) => (
                          <TableRow key={category.id}>
                            <TableCell>{category.name}</TableCell>
                            <TableCell>
                              <Chip size="small" label={category.isActive ? "Activa" : "Inactiva"} color={category.isActive ? "success" : "default"} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Editar categoría">
                                <IconButton size="small" onClick={() => openCategoryDialog(category)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar categoría">
                                <IconButton size="small" color="error" onClick={() => setCategoryToDelete(category)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={8}>
              <Card sx={dashboardCardSx}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DirectionsCar fontSize="small" />
                        <Typography variant="h6">Flota</Typography>
                      </Stack>
                      <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openVehicleDialog()}>
                        Crear vehículo
                      </Button>
                    </Stack>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Vehículo</TableCell>
                          <TableCell>Ubicacion</TableCell>
                          <TableCell>Precio por día</TableCell>
                          <TableCell>Capacidad</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vehicles.map((vehicle) => (
                          <TableRow key={vehicle.id}>
                            <TableCell>{vehicle.name}</TableCell>
                            <TableCell>{vehicle.location}</TableCell>
                            <TableCell>${Number(vehicle.pricePerDay || 0).toLocaleString("es-CO")}</TableCell>
                            <TableCell>{vehicle.passengerCapacity} pasajeros</TableCell>
                            <TableCell>
                              <Chip size="small" label={vehicle.isActive ? "Activo" : "Inactivo"} color={vehicle.isActive ? "success" : "default"} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Editar vehículo">
                                <IconButton size="small" onClick={() => openVehicleDialog(vehicle)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar vehículo">
                                <IconButton size="small" color="error" onClick={() => setVehicleToDelete(vehicle)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === "domains" && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6">Dominios</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Publica la experiencia en subdominio LYPRO o en un dominio propio.
                    </Typography>
                  </Box>
                  <Button variant="contained" size="small" startIcon={<Domain />} onClick={() => setDomainDialogOpen(true)}>
                    Agregar dominio
                  </Button>
                </Stack>
                <Alert severity="info">
                  Agrega el dominio y luego usa Verificar para confirmar el apuntador DNS. La resolución pública sigue dependiendo del host.
                </Alert>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Hostname</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {domains.map((domain) => (
                      <TableRow key={domain.hostname}>
                        <TableCell>{domain.hostname}</TableCell>
                        <TableCell>
                          <Chip size="small" label={domain.status} color={domainStatusColor(domain.status)} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => openExperiencePreview("car_rental", { config, categories, vehicles }, domain.hostname)}>Probar página</Button>
                            <Button size="small" onClick={() => void verifyDomain(domain.hostname)}>Verificar</Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === "reservations" && (
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">Solicitudes de reserva</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gestión manual de solicitudes que llegan desde el catálogo público de vehículos.
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vehículo</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Fechas</TableCell>
                      <TableCell>Entrega</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservations.map((reservation) => (
                      <TableRow key={reservation.id}>
                        <TableCell>{reservation.vehicleName}</TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">{reservation.customerName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {reservation.customerPhone}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {reservation.startDate} - {reservation.endDate}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{reservation.pickupLocation}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Devuelve en {reservation.returnLocation}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={reservation.status}
                            sx={{ minWidth: 180 }}
                            onChange={(event) => void updateReservationStatus(reservation.id, event.target.value as CarRentalReservationStatus)}
                          >
                            {Object.entries(reservationStatusLabels).map(([value, label]) => (
                              <MenuItem key={value} value={value}>
                                {label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Chip
                            size="small"
                            label={reservationStatusLabels[reservation.status]}
                            color={reservationStatusColor(reservation.status)}
                            sx={{ ml: 1, display: { xs: "none", md: "inline-flex" } }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Dialog open={categoryDialogOpen} onClose={resetCategoryDialog} fullWidth maxWidth="sm">
        <DialogTitle>{categoryEditingId ? "Editar categoría" : "Crear categoría"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField fullWidth label="Nombre" value={categoryDraft.name || ""} onChange={(event) => setCategoryDraft({ ...categoryDraft, name: event.target.value })} />
            <TextField fullWidth label="Descripción" multiline minRows={3} value={categoryDraft.description || ""} onChange={(event) => setCategoryDraft({ ...categoryDraft, description: event.target.value })} />
            <TextField select fullWidth label="Estado" value={categoryDraft.isActive === false ? "inactive" : "active"} onChange={(event) => setCategoryDraft({ ...categoryDraft, isActive: event.target.value === "active" })}>
              <MenuItem value="active">Activa</MenuItem>
              <MenuItem value="inactive">Inactiva</MenuItem>
            </TextField>
            <TextField fullWidth type="number" label="Orden" value={categoryDraft.sortOrder ?? 0} onChange={(event) => setCategoryDraft({ ...categoryDraft, sortOrder: Number(event.target.value) })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={resetCategoryDialog}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void saveCategory()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={vehicleDialogOpen} onClose={resetVehicleDialog} fullWidth maxWidth="md">
        <DialogTitle>{vehicleEditingId ? "Editar vehículo" : "Crear vehículo"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nombre" value={vehicleDraft.name || ""} onChange={(event) => setVehicleDraft({ ...vehicleDraft, name: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Ubicacion" value={vehicleDraft.location || ""} onChange={(event) => setVehicleDraft({ ...vehicleDraft, location: event.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Descripción" value={vehicleDraft.description || ""} onChange={(event) => setVehicleDraft({ ...vehicleDraft, description: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Categoría" value={vehicleDraft.categoryId || ""} onChange={(event) => setVehicleDraft({ ...vehicleDraft, categoryId: event.target.value })}>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth type="number" label="Precio por día" value={vehicleDraft.pricePerDay ?? 0} onChange={(event) => setVehicleDraft({ ...vehicleDraft, pricePerDay: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Transmisión" value={vehicleDraft.transmission || ""} onChange={(event) => setVehicleDraft({ ...vehicleDraft, transmission: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="Pasajeros" value={vehicleDraft.passengerCapacity ?? 0} onChange={(event) => setVehicleDraft({ ...vehicleDraft, passengerCapacity: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="Puertas" value={vehicleDraft.doors ?? 0} onChange={(event) => setVehicleDraft({ ...vehicleDraft, doors: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="Equipaje" value={vehicleDraft.luggageCapacity ?? 0} onChange={(event) => setVehicleDraft({ ...vehicleDraft, luggageCapacity: Number(event.target.value) })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Aire acondicionado" value={vehicleDraft.hasAirConditioning === false ? "no" : "yes"} onChange={(event) => setVehicleDraft({ ...vehicleDraft, hasAirConditioning: event.target.value === "yes" })}>
                <MenuItem value="yes">Si</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Imagen URL" value={vehicleDraft.imageUrl || ""} onChange={(event) => setVehicleDraft({ ...vehicleDraft, imageUrl: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Disponibilidad manual" value={vehicleDraft.availabilityNotes || ""} onChange={(event) => setVehicleDraft({ ...vehicleDraft, availabilityNotes: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Estado" value={vehicleDraft.isActive === false ? "inactive" : "active"} onChange={(event) => setVehicleDraft({ ...vehicleDraft, isActive: event.target.value === "active" })}>
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="number" label="Orden" value={vehicleDraft.sortOrder ?? 0} onChange={(event) => setVehicleDraft({ ...vehicleDraft, sortOrder: Number(event.target.value) })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={resetVehicleDialog}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void saveVehicle()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={domainDialogOpen} onClose={() => setDomainDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Agregar dominio</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField fullWidth label="Dominio personalizado" placeholder="carros.midominio.com" value={domainDraft} onChange={(event) => setDomainDraft(event.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDomainDialogOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void addDomain()}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(categoryToDelete)}
        title="Eliminar categoría"
        message={`Se eliminará la categoría ${categoryToDelete?.name || ""}.`}
        confirmColor="error"
        confirmText="Eliminar"
        onClose={() => setCategoryToDelete(undefined)}
        onConfirm={() => void deleteCategory()}
      />

      <ConfirmationDialog
        open={Boolean(vehicleToDelete)}
        title="Eliminar vehículo"
        message={`Se eliminará el vehículo ${vehicleToDelete?.name || ""}.`}
        confirmColor="error"
        confirmText="Eliminar"
        onClose={() => setVehicleToDelete(undefined)}
        onConfirm={() => void deleteVehicle()}
      />

      <FeedbackSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default CarRentalPage;
